import { err, ok, type Result } from 'neverthrow';
import { z } from 'zod';

import { NaiveDate } from './naive_date.js';
import { YearMonth } from './year_month.js';

export type BookYearDto = {
  end: string;
  start: string;
};

const separator = '-';

declare const bookYearBrand: unique symbol;

/**
 * Branded string type for book years.
 *
 * The string format is `"YYYYMMDD-YYYYMMDD"` (compact ISO 8601 dates joined by
 * a single hyphen) — identical to the wire format used by the Rust
 * `BookYearString` type, so values round-trip with the backend without
 * conversion.
 *
 * Operations live on the `BookYear` namespace value below; the branded
 * primitive is naturally usable as a `Map`/`Set` key.
 */
export type BookYear = string & { readonly [bookYearBrand]: true };

const BOOK_YEAR_PATTERN = /^\d{8}-\d{8}$/;

/** Same upper bound as the Rust `BookYear::new` constructor. */
const MAX_MONTHS = 18;

function monthSpan(startCompact: string, endCompact: string): number {
  const startMonths = Number(startCompact.slice(0, 4)) * 12 + Number(startCompact.slice(4, 6));
  const endMonths = Number(endCompact.slice(0, 4)) * 12 + Number(endCompact.slice(4, 6));
  return endMonths - startMonths + 1;
}

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return DAYS_IN_MONTH.at(month - 1) ?? 0;
}

/** Returns a problem description for a compact `YYYYMMDD` date, or null when valid. */
function compactDateProblem(compact: string): string | null {
  const year = Number(compact.slice(0, 4));
  const month = Number(compact.slice(4, 6));
  const day = Number(compact.slice(6, 8));
  if (month < 1 || month > 12) {
    return `month out of range in date ${compact}`;
  }
  if (day < 1 || day > daysInMonth(year, month)) {
    return `day out of range in date ${compact}`;
  }
  return null;
}

function formatString(start: NaiveDate, end: NaiveDate): string {
  return `${NaiveDate.toIsoStringBasic(start)}${separator}${NaiveDate.toIsoStringBasic(end)}`;
}

function splitString(value: BookYear): [string, string] {
  const parts = value.split(separator);
  if (parts.length !== 2) {
    throw new Error(`Invalid BookYear format: expected "YYYYMMDD-YYYYMMDD", got: ${value}`);
  }
  return [parts[0], parts[1]];
}

function parseDates(value: BookYear): [NaiveDate, NaiveDate] {
  const [start, end] = splitString(value);
  return [NaiveDate.fromIsoString(start), NaiveDate.fromIsoString(end)];
}

export const BookYear = {
  // ----- Smart constructors -----

  /**
   * Parse and validate a string in the canonical `"YYYYMMDD-YYYYMMDD"` format.
   * Use this for any value coming from outside the type-safe layer (network,
   * URL, user input).
   *
   * Validation is pure integer/string work — no Date or NaiveDate allocation —
   * so this is safe to call per row or per map key.
   */
  parse(value: string): Result<BookYear, string> {
    if (!BOOK_YEAR_PATTERN.test(value)) {
      return err(`Invalid BookYear string (expected "YYYYMMDD-YYYYMMDD"): ${value}`);
    }
    const start = value.slice(0, 8);
    const end = value.slice(9, 17);
    const startProblem = compactDateProblem(start);
    if (startProblem !== null) {
      return err(`Invalid BookYear start: ${startProblem}`);
    }
    const endProblem = compactDateProblem(end);
    if (endProblem !== null) {
      return err(`Invalid BookYear end: ${endProblem}`);
    }
    if (start > end) {
      return err(`Invalid BookYear: start date (${start}) must not be after end date (${end})`);
    }
    const endDay = Number(end.slice(6, 8));
    if (endDay !== daysInMonth(Number(end.slice(0, 4)), Number(end.slice(4, 6)))) {
      return err(`Invalid BookYear: end date (${end}) must be the last day of a month`);
    }
    if (monthSpan(start, end) > MAX_MONTHS) {
      return err(`Invalid BookYear: period is longer than ${MAX_MONTHS} months (${value})`);
    }
    return ok(value as BookYear);
  },

  /**
   * Cast a string to `BookYear` without validation. Use only for inputs
   * that are provably already in the canonical format (e.g. values just read
   * back from another `BookYear`, or freshly serialized output).
   */
  unsafeFromString(value: string): BookYear {
    return value as BookYear;
  },

  fromDates(start: NaiveDate, end: NaiveDate): Result<BookYear, string> {
    return BookYear.parse(formatString(start, end));
  },

  /** Accepts dates in extended (`YYYY-MM-DD`) or basic (`YYYYMMDD`) ISO form. */
  fromDto(dto: BookYearDto): Result<BookYear, string> {
    const start = dto.start.replaceAll(separator, '');
    const end = dto.end.replaceAll(separator, '');
    return BookYear.parse(`${start}${separator}${end}`).mapErr(
      (message) => `Invalid BookYearDto (start: ${dto.start}, end: ${dto.end}): ${message}`,
    );
  },

  // ----- Field accessors -----

  first(value: BookYear): NaiveDate {
    return NaiveDate.fromIsoString(splitString(value)[0]);
  },

  last(value: BookYear): NaiveDate {
    return NaiveDate.fromIsoString(splitString(value)[1]);
  },

  // ----- Display -----

  display(value: BookYear): string {
    return BookYear.displayFull(value);
  },

  displayFull(value: BookYear): string {
    const [first, last] = splitString(value);
    return `${formatExtended(first)} – ${formatExtended(last)}`;
  },

  displayShort(value: BookYear): string {
    const [first, last] = splitString(value);
    return `${formatExtended(first).slice(0, 7)} – ${formatExtended(last).slice(0, 7)}`;
  },

  /**
   * Compact, filesystem-safe label: `"2025"` for a calendar year, otherwise the
   * end date (`"2026-06-30"`), which identifies the book year once the company
   * is known (end-date-major, cf. `compare` and Rust `BookYear::compact_label`).
   */
  displayCompact(value: BookYear): string {
    if (BookYear.isCalendarYear(value)) {
      return value.slice(0, 4);
    }
    return BookYear.displayEndDate(value);
  },

  displayEndDate(value: BookYear): string {
    return formatExtended(splitString(value)[1]);
  },

  // ----- Period operations -----

  /** Whether `date` falls within the book year (inclusive). Pure string comparison. */
  contains(value: BookYear, date: NaiveDate): boolean {
    const compact = NaiveDate.toIsoStringBasic(date);
    return value.slice(0, 8) <= compact && compact <= value.slice(9, 17);
  },

  /**
   * End-date-major ordering: book years sort by end date, then start date.
   * The canonical compact format makes this a plain string comparison.
   */
  compare(a: BookYear, b: BookYear): number {
    const aKey = a.slice(9, 17) + a.slice(0, 8);
    const bKey = b.slice(9, 17) + b.slice(0, 8);
    if (aKey < bKey) {
      return -1;
    }
    if (aKey > bKey) {
      return 1;
    }
    return 0;
  },

  /** Ordering by end date only; book years with the same end date compare equal. */
  compareLast(a: BookYear, b: BookYear): number {
    const aEnd = a.slice(9, 17);
    const bEnd = b.slice(9, 17);
    if (aEnd < bEnd) {
      return -1;
    }
    if (aEnd > bEnd) {
      return 1;
    }
    return 0;
  },

  /** The day immediately before the book year begins (the eve of its first day). */
  dayBefore(value: BookYear): NaiveDate {
    return NaiveDate.previous(BookYear.first(value));
  },

  /** True when `next` begins the day immediately after `value` ends — no gap, no overlap. */
  isSequential(value: BookYear, next: BookYear): boolean {
    return NaiveDate.next(BookYear.last(value)) === BookYear.first(next);
  },

  /** True when the two book years share at least one day. */
  overlaps(value: BookYear, other: BookYear): boolean {
    return (
      BookYear.last(value) >= BookYear.first(other) && BookYear.last(other) >= BookYear.first(value)
    );
  },

  // ----- Domain operations -----

  next(value: BookYear): BookYear {
    const currentEnd = BookYear.last(value);
    const start = NaiveDate.next(currentEnd);
    let end: NaiveDate;
    if (NaiveDate.isLeapYear(currentEnd)) {
      // If current end is a leap year and the end date is the last of February, we must go one previous before updating the year.
      // Otherwise we will try to create a NaiveDate from the string `2025-02-29` which will result in `2025-03-01` instead of `2025-02-28`.
      if (NaiveDate.month(currentEnd) === 2 && NaiveDate.date(currentEnd) === 29) {
        end = NaiveDate.updateYear(NaiveDate.previous(currentEnd), 1);
      } else {
        end = NaiveDate.updateYear(currentEnd, 1);
      }
    } else {
      end = NaiveDate.updateYear(currentEnd, 1);
    }
    return formatString(start, end) as BookYear;
  },

  isSplit(value: BookYear): boolean {
    const [first, last] = parseDates(value);
    const dateAfterLast = NaiveDate.next(last);
    return !(
      NaiveDate.month(dateAfterLast) === NaiveDate.month(first) &&
      NaiveDate.date(dateAfterLast) === NaiveDate.date(first)
    );
  },

  isLong(value: BookYear): boolean {
    const [first, last] = parseDates(value);
    const nextYearFirst = NaiveDate.updateYear(first, 1);
    return nextYearFirst <= last;
  },

  isCalendarYear(value: BookYear): boolean {
    return (
      value.slice(0, 4) === value.slice(9, 13) &&
      value.slice(4, 8) === '0101' &&
      value.slice(13, 17) === '1231'
    );
  },

  toYearMonths(value: BookYear): YearMonth[] {
    const [first, last] = parseDates(value);
    const yearMonths: YearMonth[] = [];
    let current = YearMonth.fromNaiveDate(first);
    const end = YearMonth.fromNaiveDate(last);
    while (current !== end) {
      yearMonths.push(current);
      current = YearMonth.next(current);
    }
    yearMonths.push(current);
    return yearMonths;
  },

  toMonthRanges(value: BookYear): [NaiveDate, NaiveDate][] {
    const [first] = parseDates(value);
    const firstMonth = YearMonth.fromNaiveDate(first);
    const firstMonthEnd = NaiveDate.fromDate(YearMonth.last(firstMonth));
    const ranges: [NaiveDate, NaiveDate][] = [[first, firstMonthEnd] as const];
    const restMonths = BookYear.toYearMonths(value).slice(1);
    for (const month of restMonths) {
      const monthFirst = NaiveDate.fromDate(YearMonth.first(month));
      const monthLast = NaiveDate.fromDate(YearMonth.last(month));
      ranges.push([monthFirst, monthLast] as const);
    }
    return ranges;
  },

  relativeMonthNumber(value: BookYear, yearMonth: YearMonth): number {
    const [first] = parseDates(value);
    const start = YearMonth.fromYearAndMonth(NaiveDate.year(first), NaiveDate.month(first));
    return YearMonth.monthsUntil(start, yearMonth) + 1;
  },

  /**
   * Returns the latest (most recent) book year that has been completed.
   * A book year is considered completed if its end date is less than or equal to today.
   * If no book years are completed, returns null.
   */
  latestCompleted(bookYears: BookYear[], today: NaiveDate = NaiveDate.now()): BookYear | null {
    if (bookYears.length === 0) {
      return null;
    }
    const completed = bookYears.filter((bookYear) => BookYear.last(bookYear) <= today);
    if (completed.length === 0) {
      return null;
    }
    return completed.reduce((latest, current) =>
      BookYear.last(current) > BookYear.last(latest) ? current : latest,
    );
  },

  // ----- Coders (cf. simple_id.ts) -----

  /** Zod schema for the canonical string form; validates and brands. */
  schema: z.string().transform((value, ctx): BookYear => {
    const result = BookYear.parse(value);
    if (result.isErr()) {
      ctx.addIssue({ code: 'custom', message: result.error });
      return z.NEVER;
    }
    return result.value;
  }),
};

function formatExtended(basicIsoDate: string): string {
  return NaiveDate.fromIsoString(basicIsoDate);
}
